import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatBRL, formatDateBR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { ExpenseFormDialog } from "../components/ExpenseFormDialog";
import { Button } from "../components/ui/button";

export default function Expenses() {
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ q: "", start: "", end: "" });
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const openNew = () => { setEditing(null); setOpen(true); };
    const openEdit = (e) => { setEditing(e); setOpen(true); };
    const onOpenChange = (o) => { setOpen(o); if (!o) setEditing(null); };

    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);

    const { data: expenses = [] } = useQuery({ queryKey: ["expenses", filters], queryFn: async () => (await api.get(`/expenses?${params}`)).data });

    const remove = async (id) => {
        try {
            await api.delete(`/expenses/${id}`);
            qc.invalidateQueries({ queryKey: ["expenses"] });
            qc.invalidateQueries({ queryKey: ["summary"] });
            toast.success("Lançamento removido");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div>
            <PageHeader
                overline="Financeiro"
                title="Extrato & Despesas"
                description="Registre despesas e receitas diárias. Use a voz para lançar sem digitar."
                action={
                    <Button onClick={openNew} className="rounded-full px-6" data-testid="add-expense-btn"><Plus className="mr-2 h-4 w-4" /> Novo lançamento</Button>
                }
            />

            <ExpenseFormDialog open={open} onOpenChange={onOpenChange} expense={editing} />

            <Filters q={filters.q} start={filters.start} end={filters.end} onChange={setFilters} placeholder="Buscar por descrição ou categoria..." />

            <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-white" data-testid="expenses-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                            <th className="px-5 py-4">Data</th>
                            <th className="px-5 py-4">Descrição</th>
                            <th className="px-5 py-4">Categoria</th>
                            <th className="px-5 py-4">Lançado por</th>
                            <th className="px-5 py-4 text-right">Valor</th>
                            <th className="px-5 py-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {expenses.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">Nenhum lançamento encontrado.</td></tr>
                        )}
                        {expenses.map((e) => (
                            <tr key={e.id} className="border-b border-border/50 last:border-0 hover:bg-muted/40" data-testid={`expense-row-${e.id}`}>
                                <td className="px-5 py-4 font-mono-nums text-muted-foreground">{formatDateBR(e.date)}</td>
                                <td className="px-5 py-4 font-medium">{e.description}</td>
                                <td className="px-5 py-4"><span className="rounded-full bg-muted px-3 py-1 text-xs">{e.category}</span></td>
                                <td className="px-5 py-4 text-sm text-muted-foreground" data-testid={`expense-author-${e.id}`}>{e.created_by || "—"}</td>
                                <td className={`px-5 py-4 text-right font-mono-nums font-semibold ${e.tipo === "receita" ? "text-income" : "text-expense"}`}>
                                    {e.tipo === "receita" ? "+" : "-"}{formatBRL(e.amount)}
                                </td>
                                <td className="px-5 py-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <button onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`} className="rounded-lg p-2 text-muted-foreground hover:bg-accent/10 hover:text-accent">
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`} className="rounded-lg p-2 text-muted-foreground hover:bg-expense/10 hover:text-expense">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
